import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AppView, HealthProfessional, CallRequestData, CallConfirmation } from '../types';

// Extended state interface with error and loading states
export interface AppState {
  currentView: AppView;
  selectedHealthProfessional: HealthProfessional | null;
  callRequestData: CallRequestData | null;
  callConfirmation: CallConfirmation | null;
  healthProfessionals: HealthProfessional[];
  isLoading: boolean;
  error: string | null;
  selectedFilters: string[];
}

// Action types for state management
export type AppAction =
  | { type: 'SET_VIEW'; payload: AppView }
  | { type: 'SET_SELECTED_PROFESSIONAL'; payload: HealthProfessional | null }
  | { type: 'SET_CALL_REQUEST_DATA'; payload: CallRequestData | null }
  | { type: 'SET_CALL_CONFIRMATION'; payload: CallConfirmation | null }
  | { type: 'SET_HEALTH_PROFESSIONALS'; payload: HealthProfessional[] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_FILTERS'; payload: string[] }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RESET_STATE' }
  | { type: 'START_OVER' };

// Initial state
const initialState: AppState = {
  currentView: 'landing',
  selectedHealthProfessional: null,
  callRequestData: null,
  callConfirmation: null,
  healthProfessionals: [],
  isLoading: false,
  error: null,
  selectedFilters: [],
};

// State reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_VIEW':
      return {
        ...state,
        currentView: action.payload,
        error: null, // Clear errors when navigating
      };

    case 'SET_SELECTED_PROFESSIONAL':
      return {
        ...state,
        selectedHealthProfessional: action.payload,
      };

    case 'SET_CALL_REQUEST_DATA':
      return {
        ...state,
        callRequestData: action.payload,
      };

    case 'SET_CALL_CONFIRMATION':
      return {
        ...state,
        callConfirmation: action.payload,
      };

    case 'SET_HEALTH_PROFESSIONALS':
      return {
        ...state,
        healthProfessionals: action.payload,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false, // Stop loading when error occurs
      };

    case 'SET_FILTERS':
      return {
        ...state,
        selectedFilters: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'RESET_STATE':
      return {
        ...initialState,
        healthProfessionals: state.healthProfessionals, // Keep loaded professionals
      };

    case 'START_OVER':
      return {
        ...initialState,
        healthProfessionals: state.healthProfessionals, // Keep loaded professionals
        currentView: 'landing',
      };

    default:
      return state;
  }
}

// Context type
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Context provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the app context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Custom hooks for specific state operations
export const useAppState = () => {
  const { state } = useAppContext();
  return state;
};

export const useAppActions = () => {
  const { dispatch } = useAppContext();

  return {
    setView: (view: AppView) => dispatch({ type: 'SET_VIEW', payload: view }),
    
    setSelectedProfessional: (professional: HealthProfessional | null) =>
      dispatch({ type: 'SET_SELECTED_PROFESSIONAL', payload: professional }),
    
    setCallRequestData: (data: CallRequestData | null) =>
      dispatch({ type: 'SET_CALL_REQUEST_DATA', payload: data }),
    
    setCallConfirmation: (confirmation: CallConfirmation | null) =>
      dispatch({ type: 'SET_CALL_CONFIRMATION', payload: confirmation }),
    
    setHealthProfessionals: (professionals: HealthProfessional[]) =>
      dispatch({ type: 'SET_HEALTH_PROFESSIONALS', payload: professionals }),
    
    setLoading: (loading: boolean) =>
      dispatch({ type: 'SET_LOADING', payload: loading }),
    
    setError: (error: string | null) =>
      dispatch({ type: 'SET_ERROR', payload: error }),
    
    setFilters: (filters: string[]) =>
      dispatch({ type: 'SET_FILTERS', payload: filters }),
    
    clearError: () => dispatch({ type: 'CLEAR_ERROR' }),
    
    resetState: () => dispatch({ type: 'RESET_STATE' }),
    
    startOver: () => dispatch({ type: 'START_OVER' }),
  };
};

export default AppContext;